<template>
  <div class="user-management">
    <div class="toolbar">
      <span class="p-input-icon-left search-field">
        <i class="pi pi-search" />
        <InputText
          v-model="userSearchTerm"
          placeholder="Benutzer, Kontaktperson, Recht oder Mannschaft suchen"
        />
      </span>
      <Button
        v-if="userSearchTerm && userSearchTerm.length > 0"
        class="p-button-text p-button-sm"
        label="Suche löschen"
        icon="pi pi-times"
        @click="userSearchTerm = ''"
      />
      <Button
        class="p-button-outlined p-button-sm"
        label="Kontaktpersonen ohne Benutzer"
        icon="pi pi-id-card"
        @click="showContactPersonsWithoutUserDialog()"
      />
      <Button
        :class="showDeactivatedUsers ? 'p-button-sm' : 'p-button-outlined p-button-sm'"
        :label="showDeactivatedUsers ? 'Aktive Benutzer' : 'Deaktivierte Benutzer'"
        :icon="showDeactivatedUsers ? 'pi pi-users' : 'pi pi-ban'"
        @click="showDeactivatedUsers = !showDeactivatedUsers"
      />
      <span class="user-count">
        <template v-if="usersLoading">
          <i class="pi pi-spin pi-spinner" /> Benutzer werden geladen ...
        </template>
        <template v-else>
          {{ filteredUsers.length }} / {{ usersInScope.length }}
          {{ showDeactivatedUsers ? "deaktivierte Benutzer" : "aktive Benutzer" }}
        </template>
      </span>
    </div>

    <DataTable
      class="p-datatable-sm"
      :value="filteredUsers"
      :loading="usersLoading"
      v-model:expandedRows="expandedRows"
      @rowExpand="onRowExpand"
      @rowCollapse="onRowCollapse"
      dataKey="id"
      responsiveLayout="stack"
      breakpoint="850px"
      sortField="userName"
      :sortOrder="1"
      scrollable
      scrollHeight="65vh"
    >
      <template #empty>
        <span v-if="usersLoading">Benutzer werden geladen ...</span>
        <span v-else-if="showDeactivatedUsers">Kein deaktivierter Benutzer gefunden.</span>
        <span v-else>Keine Benutzer für die aktuelle Suche gefunden.</span>
      </template>
      <Column expander style="width: 4rem" />
      <Column field="userName" header="Benutzername" sortable />
      <Column field="contactPersonName" header="Kontaktperson" sortable>
        <template #body="slotProps">
          <div v-if="slotProps.data.contactPersonName" class="contact-person">
            <span>{{ slotProps.data.contactPersonName }}</span>
            <span v-if="slotProps.data.contactPersonTeams" class="contact-person-teams">
              {{ slotProps.data.contactPersonTeams }}
            </span>
          </div>
          <span v-else>-</span>
        </template>
      </Column>
      <Column field="lastLoggedIn" header="Letzter Login" sortable>
        <template #body="slotProps">
          {{ formatDateTimeInfo(slotProps.data.lastLoggedIn) || "nie" }}
        </template>
      </Column>
      <Column header="Funktionen">
        <template #body="slotProps">
          <div class="permission-preview">
            <span
              v-for="userRoleLabel in getUserRoleLabels(slotProps.data)"
              :key="slotProps.data.id + ':role:' + userRoleLabel"
              class="permission-chip"
            >
              {{ userRoleLabel }}
            </span>
            <span v-if="getUserRoleLabels(slotProps.data).length < 1">-</span>
          </div>
        </template>
      </Column>
      <Column header="Rechte-Übersicht">
        <template #body="slotProps">
          <div class="permission-preview">
            <span
              v-for="permissionLabel in getPermissionPreviewItems(slotProps.data)"
              :key="slotProps.data.id + ':' + permissionLabel"
              class="permission-chip"
            >
              {{ permissionLabel }}
            </span>
            <span
              v-if="getPermissionOverflowCount(slotProps.data) > 0"
              class="permission-overflow"
            >
              +{{ getPermissionOverflowCount(slotProps.data) }} weitere
            </span>
          </div>
        </template>
      </Column>
      <Column header="Aktionen" style="width: 17rem">
        <template #body="slotProps">
          <Button
            style="margin: 2px 2px"
            icon="pi pi-key"
            v-tooltip.top="'Rechte und Funktionen bearbeiten'"
            class="p-button-rounded p-button-success"
            @click="showPermissionsAndRolesDialog(slotProps.data)"
          />
          <Button
            style="margin: 2px 2px"
            icon="pi pi-id-card"
            v-tooltip.top="'Kontaktperson zuweisen'"
            class="p-button-rounded p-button-help"
            @click="showAssignContactPersonDialog(slotProps.data)"
          />
          <Button
            style="margin: 2px 2px"
            icon="pi pi-envelope"
            v-tooltip.top="'Zugangsdaten per E-Mail senden'"
            class="p-button-rounded"
            @click="showCredentialsMailDialog(slotProps.data)"
          />
          <Button
            style="margin: 2px 2px"
            :icon="slotProps.data.active ? 'pi pi-ban' : 'pi pi-check-circle'"
            v-tooltip.top="
              slotProps.data.active ? 'Benutzer deaktivieren' : 'Benutzer aktivieren'
            "
            :class="
              slotProps.data.active
                ? 'p-button-rounded p-button-warning'
                : 'p-button-rounded p-button-info'
            "
            @click="showUserActivationDialog(slotProps.data)"
          />
        </template>
      </Column>
      <template #expansion="slotProps">
        <DataTable class="p-datatable-sm" :value="getSortedPermissions(slotProps.data)" stripedRows>
          <template #empty> Der Benutzer hat kein Recht. </template>
          <Column header="Rechtetyp">
            <template #body="slotProps2">
              {{ translatePermissionRole(slotProps2.data.roleName) }}
            </template>
          </Column>
          <Column field="entityRepresentation" header="Rechte auf">
            <template #body="slotProps2">
              {{ slotProps2.data.entityRepresentation || "-" }}
            </template>
          </Column>
        </DataTable>
      </template>
    </DataTable>
  </div>

  <Dialog
    id="userActivationDialog"
    modal
    v-model:visible="shouldShowUserActivationDialog"
    style="min-width: 300px; max-width: 600px"
    :closable="true"
  >
    <template #header>
      <h3>{{ currentUser.active ? "Benutzer deaktivieren" : "Benutzer aktivieren" }}</h3>
    </template>

    <template v-if="currentUser.active">
      Soll der Benutzer {{ currentUser.userName }} wirklich deaktiviert werden? Er kann
      sich danach nicht mehr anmelden und wird nicht mehr als Empfänger im Postfach
      geführt. Seine Rechte bleiben erhalten, so dass er jederzeit wieder aktiviert
      werden kann.
    </template>
    <template v-else>
      Soll der Benutzer {{ currentUser.userName }} wieder aktiviert werden? Er kann sich
      danach mit seinen bisherigen Rechten wieder anmelden.
    </template>

    <template #footer>
      <Button
        :label="currentUser.active ? 'Deaktivieren' : 'Aktivieren'"
        icon="pi pi-check"
        class="p-button-text"
        @click="closeUserActivationDialog(true)"
      />
      <Button
        label="Abbrechen"
        icon="pi pi pi-times"
        @click="closeUserActivationDialog(false)"
      />
    </template>
  </Dialog>
  <Dialog
    id="permissionsAndRolesDialog"
    modal
    v-model:visible="shouldShowPermissionsAndRolesDialog"
    style="min-width: 300px; max-width: 700px"
    :closable="true"
  >
    <template #header>
      <h3>Rechte und Funktionen für {{ currentUser.userName }}</h3>
    </template>

    <h4 class="dialog-section">Funktionen</h4>
    <div class="dialog-hint">
      Über die Funktionen ist der Benutzer im Postfach als Empfänger erreichbar.
    </div>
    <div
      v-for="userRole in userRoles"
      :key="'userRole' + userRole.name"
      class="user-role-option"
    >
      <Checkbox
        :inputId="'userRole' + userRole.name"
        :value="userRole.name"
        v-model="currentUserRoles"
      />
      <label :for="'userRole' + userRole.name">{{ userRole.label }}</label>
    </div>

    <h4 class="dialog-section">Rechte</h4>
    <div v-if="currentPermissions.length < 1" class="dialog-hint">
      Der Benutzer hat noch kein Recht.
    </div>
    <div
      v-for="(permission, index) in currentPermissions"
      :key="'currentPermission' + index"
      class="pending-permission"
    >
      <span>{{ formatPermissionLabel(permission) }}</span>
      <Button
        icon="pi pi-trash"
        v-tooltip.top="'Recht entfernen'"
        class="p-button-rounded p-button-text p-button-warning p-button-sm"
        @click="removeCurrentUserPermission(index)"
      />
    </div>
    <Dropdown
      v-model="currentPermissionRole"
      :options="permissionRoles"
      :editable="false"
      optionLabel="displayName"
      placeholder="Rechtetyp"
      style="width: 100%; margin: 10px 0px"
      @change="currentPermissionRoleChanged"
    />
    <Dropdown
      v-if="currentPermissionEntities.length > 0"
      v-model="currentPermissionEntity"
      :options="currentPermissionEntities"
      optionLabel="representation"
      :editable="false"
      :filter="currentPermissionEntities.length >= 10"
      style="width: 100%; margin: 10px 0px"
    />
    <Button
      label="Recht hinzufügen"
      icon="pi pi-plus-circle"
      class="p-button-text"
      @click="addCurrentUserPermission()"
    />
    <div v-if="dialogError" class="dialog-error">{{ dialogError }}</div>

    <template #footer>
      <Button
        label="Speichern"
        icon="pi pi-check"
        class="p-button-text"
        @click="savePermissionsAndRoles()"
      />
      <Button label="Abbrechen" icon="pi pi pi-times" @click="showDialog = ''" />
    </template>
  </Dialog>
  <Dialog
    id="newUserDialog"
    modal
    v-model:visible="shouldShowNewUserDialog"
    style="min-width: 300px; max-width: 600px"
    :closable="true"
  >
    <template #header>
      <h3>Neuen Benutzer anlegen</h3>
    </template>

    <div class="dialog-field">
      <label for="newUserName">Benutzername</label>
      <InputText id="newUserName" v-model="newUser.userName" style="width: 100%" />
    </div>
    <div class="dialog-field">
      <label for="newUserPassword">Passwort</label>
      <div class="password-row">
        <InputText id="newUserPassword" v-model="newUser.password" style="flex: 1 1 auto" />
        <Button
          icon="pi pi-refresh"
          v-tooltip.top="'Passwort generieren'"
          class="p-button-rounded p-button-text"
          @click="generatePasswordFor('newUser')"
        />
      </div>
    </div>

    <div class="dialog-field">
      <label for="newUserContactPerson">Kontaktperson (optional)</label>
      <Dropdown
        id="newUserContactPerson"
        v-model="newUser.contactPerson"
        :options="contactPersons"
        optionLabel="representation"
        :editable="false"
        :filter="true"
        :showClear="true"
        placeholder="Keine Kontaktperson"
        style="width: 100%"
      />
    </div>

    <h4 class="dialog-section">Rechte</h4>
    <div v-if="newUser.permissions.length < 1" class="dialog-hint">
      Für den neuen Benutzer wurde noch kein Recht ausgewählt.
    </div>
    <div
      v-for="(permission, index) in newUser.permissions"
      :key="'newUserPermission' + index"
      class="pending-permission"
    >
      <span>{{ formatPermissionLabel(permission) }}</span>
      <Button
        icon="pi pi-trash"
        v-tooltip.top="'Recht entfernen'"
        class="p-button-rounded p-button-text p-button-warning p-button-sm"
        @click="removeNewUserPermission(index)"
      />
    </div>
    <Dropdown
      v-model="currentPermissionRole"
      :options="permissionRoles"
      :editable="false"
      optionLabel="displayName"
      placeholder="Rechtetyp"
      style="width: 100%; margin: 10px 0px"
      @change="currentPermissionRoleChanged"
    />
    <Dropdown
      v-if="currentPermissionEntities.length > 0"
      v-model="currentPermissionEntity"
      :options="currentPermissionEntities"
      optionLabel="representation"
      :editable="false"
      :filter="currentPermissionEntities.length >= 10"
      style="width: 100%; margin: 10px 0px"
    />
    <Button
      label="Recht hinzufügen"
      icon="pi pi-plus-circle"
      class="p-button-text"
      @click="addNewUserPermission()"
    />
    <div v-if="dialogError" class="dialog-error">{{ dialogError }}</div>

    <template #footer>
      <Button
        label="Anlegen"
        icon="pi pi-check"
        class="p-button-text"
        @click="createNewUser()"
      />
      <Button
        label="Abbrechen"
        icon="pi pi pi-times"
        @click="closeDialog()"
      />
    </template>
  </Dialog>
  <Dialog
    id="contactPersonsWithoutUserDialog"
    modal
    v-model:visible="shouldShowContactPersonsWithoutUserDialog"
    style="min-width: 300px; max-width: 900px"
    :closable="true"
  >
    <template #header>
      <h3>Kontaktpersonen ohne Benutzer</h3>
    </template>

    <div class="toolbar">
      <span class="p-input-icon-left search-field">
        <i class="pi pi-search" />
        <InputText
          v-model="contactPersonSearchTerm"
          placeholder="Kontaktperson oder Mannschaft suchen"
        />
      </span>
      <span class="user-count">
        {{ filteredContactPersonsWithoutUser.length }} /
        {{ contactPersonsWithoutUser.length }} Kontaktpersonen
      </span>
    </div>
    <DataTable
      class="p-datatable-sm"
      :value="filteredContactPersonsWithoutUser"
      dataKey="id"
      responsiveLayout="stack"
      breakpoint="850px"
      sortField="name"
      :sortOrder="1"
      scrollable
      scrollHeight="55vh"
    >
      <template #empty> Jede Kontaktperson hat einen zugewiesenen Benutzer. </template>
      <Column field="name" header="Kontaktperson" sortable />
      <Column field="teamNames" header="Mannschaft(en)" sortable />
      <Column field="mailAddress" header="E-Mail">
        <template #body="slotProps">
          {{ slotProps.data.mailAddress || "-" }}
        </template>
      </Column>
      <Column field="managingUserNames" header="Benutzer der Mannschaft" sortable>
        <template #body="slotProps">
          {{ slotProps.data.managingUserNames || "-" }}
        </template>
      </Column>
      <Column header="Aktionen" style="width: 8rem">
        <template #body="slotProps">
          <Button
            style="margin: 2px 2px"
            icon="pi pi-user-plus"
            v-tooltip.top="'Benutzer anlegen'"
            class="p-button-rounded p-button-success"
            @click="showNewUserDialogForContactPerson(slotProps.data)"
          />
          <Button
            v-if="slotProps.data.managingUsers && slotProps.data.managingUsers.length > 0"
            style="margin: 2px 2px"
            icon="pi pi-link"
            v-tooltip.top="'Zuweisung herstellen'"
            class="p-button-rounded p-button-help"
            @click="showConfirmAssignmentDialog(slotProps.data)"
          />
        </template>
      </Column>
    </DataTable>
    <div class="dialog-hint">
      Aufgeführt sind Kontaktpersonen aktiver Mannschaften, denen kein Benutzer
      zugewiesen ist. Steht rechts ein Benutzername, betreut dieser Benutzer die
      Mannschaft bereits - dann fehlt nur die Zuweisung.
    </div>

    <template #footer>
      <Button label="Schließen" icon="pi pi-check" @click="closeDialog()" />
    </template>
  </Dialog>
  <Dialog
    id="confirmAssignmentDialog"
    modal
    v-model:visible="shouldShowConfirmAssignmentDialog"
    style="min-width: 300px; max-width: 600px"
    :closable="true"
  >
    <template #header>
      <h3>Zuweisung herstellen</h3>
    </template>

    <p v-if="pendingAssignment.users.length > 1">
      Die Kontaktperson {{ pendingAssignment.contactPersonName }} wird dem
      ausgewählten Benutzer zugewiesen.
    </p>
    <p v-else-if="pendingAssignment.user">
      Die Kontaktperson {{ pendingAssignment.contactPersonName }} wird dem Benutzer
      {{ pendingAssignment.user.userName }} zugewiesen.
    </p>
    <div v-if="pendingAssignment.users.length > 1" class="dialog-field">
      <label for="pendingAssignmentUser">Benutzer</label>
      <Dropdown
        id="pendingAssignmentUser"
        v-model="pendingAssignment.user"
        :options="pendingAssignment.users"
        optionLabel="userName"
        :editable="false"
        style="width: 100%"
      />
    </div>
    <div
      v-if="pendingAssignment.user && pendingAssignment.user.assignedContactPersonName"
      class="dialog-warning"
    >
      Die bisherige Zuweisung des Benutzers {{ pendingAssignment.user.userName }} auf
      {{ pendingAssignment.user.assignedContactPersonName }} wird dabei überschrieben.
    </div>
    <div v-if="dialogError" class="dialog-error">{{ dialogError }}</div>

    <template #footer>
      <Button
        label="Zuweisen"
        icon="pi pi-check"
        class="p-button-text"
        @click="confirmAssignment()"
      />
      <Button label="Abbrechen" icon="pi pi pi-times" @click="closeDialog()" />
    </template>
  </Dialog>
  <Dialog
    id="assignContactPersonDialog"
    modal
    v-model:visible="shouldShowAssignContactPersonDialog"
    style="min-width: 300px; max-width: 600px"
    :closable="true"
  >
    <template #header>
      <h3>Kontaktperson für {{ currentUser.userName }}</h3>
    </template>

    <div v-if="currentUser.contactPersonName" class="dialog-hint">
      Aktuell zugewiesen: {{ currentUser.contactPersonName }}
      <span v-if="currentUser.contactPersonTeams">
        ({{ currentUser.contactPersonTeams }})
      </span>
    </div>
    <div v-else class="dialog-hint">Aktuell ist keine Kontaktperson zugewiesen.</div>
    <div class="dialog-field">
      <Dropdown
        v-model="currentContactPerson"
        :options="contactPersons"
        optionLabel="representation"
        :editable="false"
        :filter="true"
        placeholder="Kontaktperson auswählen"
        style="width: 100%"
      />
    </div>
    <div v-if="dialogError" class="dialog-error">{{ dialogError }}</div>

    <template #footer>
      <Button
        label="Zuweisen"
        icon="pi pi-check"
        class="p-button-text"
        @click="assignContactPerson()"
      />
      <Button label="Abbrechen" icon="pi pi pi-times" @click="closeDialog()" />
    </template>
  </Dialog>
  <Dialog
    id="credentialsMailDialog"
    modal
    v-model:visible="shouldShowCredentialsMailDialog"
    style="min-width: 300px; max-width: 600px"
    :closable="true"
  >
    <template #header>
      <h3>Zugangsdaten für {{ currentUser.userName }} senden</h3>
    </template>

    <div class="dialog-hint">
      Beim Senden wird das Passwort des Benutzers auf den hier angegebenen Wert gesetzt.
    </div>
    <div class="dialog-field">
      <label for="credentialsPassword">Neues Passwort</label>
      <div class="password-row">
        <InputText
          id="credentialsPassword"
          v-model="credentialsMail.password"
          style="flex: 1 1 auto"
        />
        <Button
          icon="pi pi-refresh"
          v-tooltip.top="'Passwort generieren'"
          class="p-button-rounded p-button-text"
          @click="generatePasswordFor('credentialsMail')"
        />
      </div>
    </div>
    <div class="dialog-field">
      <label for="credentialsMailAddress">E-Mail-Adresse</label>
      <InputText
        id="credentialsMailAddress"
        v-model="credentialsMail.mailAddress"
        style="width: 100%"
      />
    </div>
    <div class="dialog-field checkbox-row">
      <Checkbox
        inputId="storeForUser"
        v-model="credentialsMail.storeMailAddressForUser"
        :binary="true"
      />
      <label for="storeForUser">E-Mail-Adresse beim Benutzer übernehmen</label>
    </div>
    <div v-if="credentialsMail.contactPersonName" class="dialog-field checkbox-row">
      <Checkbox
        inputId="storeForContactPerson"
        v-model="credentialsMail.storeMailAddressForContactPerson"
        :binary="true"
      />
      <label for="storeForContactPerson">
        E-Mail-Adresse bei der Kontaktperson {{ credentialsMail.contactPersonName }}
        <span v-if="credentialsMail.contactPersonMailAddress">
          (aktuell {{ credentialsMail.contactPersonMailAddress }})
        </span>
        übernehmen
      </label>
    </div>
    <div v-if="dialogError" class="dialog-error">{{ dialogError }}</div>

    <template #footer>
      <Button
        label="Senden"
        icon="pi pi-check"
        class="p-button-text"
        :disabled="credentialsMailIsSending"
        @click="sendCredentialsMail()"
      />
      <Button label="Abbrechen" icon="pi pi pi-times" @click="closeDialog()" />
    </template>
  </Dialog>
  <Dialog
    id="infoDialog"
    modal
    v-model:visible="shouldShowInfoDialog"
    style="min-width: 300px; max-width: 600px"
    :closable="true"
  >
    <template #header>
      <h3>Hinweis</h3>
    </template>

    {{ infoMessage }}

    <template #footer>
      <Button label="Schließen" icon="pi pi-check" @click="closeDialog()" />
    </template>
  </Dialog>
</template>

<script>
import { useMenuService } from "../../services/MenuService/UseMenuService";
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import FailureHandler from "../../services/ServerConnection/FailureHandler";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";

const PERMISSION_ROLE_LABELS = {
  ADMIN: "Administrator",
  USER_ADMIN: "Benutzerverwaltung",
  PRESEASON_MANAGER: "Vorsaison-Manager",
  SEASON_MANAGER: "Saison-Manager",
  LEAGUE_MANAGER: "Liga-Manager",
  LEAGUE_MAINTAINER: "Liga-Verwalter",
  ASSOCIATION_MANAGER: "Vereins-Manager",
  TEAM_MANAGER: "Team-Manager",
};
export default {
  name: "UserManagementView",
  components: {
    DataTable,
    Column,
    Button,
    Dialog,
    Dropdown,
    InputText,
    Checkbox,
  },
  computed: {
    shouldShowUserActivationDialog() {
      return this.showDialog == "userActivation";
    },
    shouldShowPermissionsAndRolesDialog: {
      get() {
        return this.showDialog == "permissionsAndRoles";
      },
      set(visible) {
        this.showDialog = visible ? "permissionsAndRoles" : "";
      },
    },
    shouldShowNewUserDialog: {
      get() {
        return this.showDialog == "newUser";
      },
      set(visible) {
        if (visible) {
          this.showDialog = "newUser";
        } else {
          this.closeDialog();
        }
      },
    },
    shouldShowConfirmAssignmentDialog: {
      get() {
        return this.showDialog == "confirmAssignment";
      },
      set(visible) {
        if (visible) {
          this.showDialog = "confirmAssignment";
        } else {
          this.closeDialog();
        }
      },
    },
    shouldShowContactPersonsWithoutUserDialog: {
      get() {
        return this.showDialog == "contactPersonsWithoutUser";
      },
      set(visible) {
        this.showDialog = visible ? "contactPersonsWithoutUser" : "";
      },
    },
    filteredContactPersonsWithoutUser() {
      const term = this.contactPersonSearchTerm
        ? this.contactPersonSearchTerm.trim().toLowerCase()
        : "";
      if (!term) {
        return this.contactPersonsWithoutUser;
      }
      return this.contactPersonsWithoutUser.filter(
        (contactPerson) =>
          (contactPerson.name && contactPerson.name.toLowerCase().includes(term)) ||
          (contactPerson.teamNames &&
            contactPerson.teamNames.toLowerCase().includes(term))
      );
    },
    shouldShowAssignContactPersonDialog: {
      get() {
        return this.showDialog == "assignContactPerson";
      },
      set(visible) {
        this.showDialog = visible ? "assignContactPerson" : "";
      },
    },
    shouldShowCredentialsMailDialog: {
      get() {
        return this.showDialog == "credentialsMail";
      },
      set(visible) {
        this.showDialog = visible ? "credentialsMail" : "";
      },
    },
    shouldShowInfoDialog: {
      get() {
        return this.showDialog == "info";
      },
      set(visible) {
        this.showDialog = visible ? "info" : "";
      },
    },
    usersInScope() {
      return this.users.filter((user) => !!user.active !== this.showDeactivatedUsers);
    },
    filteredUsers() {
      const term = this.userSearchTerm ? this.userSearchTerm.trim().toLowerCase() : "";
      if (!term) {
        return this.usersInScope;
      }
      return this.usersInScope.filter((user) => this.userMatchesSearchTerm(user, term));
    },
  },
  created() {
    this.updateMainMenu();
    this.loadUsers(true);
    this.loadPermissionRoles();
    this.loadUserRoles();
  },
  data() {
    return {
      users: [],
      usersLoading: false,
      permissionRoles: [],
      userRoles: [],
      currentUserRoles: [],
      currentPermissionEntities: [],
      currentUser: {},
      currentPermissions: [],
      currentPermissionRole: null,
      currentPermissionEntity: {},
      showDialog: "",
      expandedRows: [],
      userSearchTerm: "",
      showDeactivatedUsers: false,
      newUser: { userName: "", password: "", permissions: [], contactPerson: null },
      contactPersons: [],
      contactPersonsRequest: null,
      currentContactPerson: null,
      contactPersonsWithoutUser: [],
      contactPersonSearchTerm: "",
      pendingAssignment: { contactPersonId: null, contactPersonName: "", users: [], user: null },
      returnDialog: "",
      credentialsMail: {},
      credentialsMailIsSending: false,
      dialogError: "",
      infoMessage: "",
    };
  },
  props: {},
  methods: {
    onRowExpand(event) {
      this.expandedRows[event.data.id] = true;
    },
    onRowCollapse(event) {
      delete this.expandedRows[event.data.id];
    },
    showUserActivationDialog(user) {
      this.currentUser = user;
      this.showDialog = "userActivation";
    },
    closeUserActivationDialog(execute) {
      if (!execute) {
        this.showDialog = "";
        return;
      }
      ServerConnection.put("admin/user/" + this.currentUser.id + "/active", {
        active: !this.currentUser.active,
      })
        .then(() => {
          this.showDialog = "";
          this.loadUsers(true);
        })
        .catch((error) => {
          if (error.response && error.response.status == 400) {
            this.infoMessage = error.response.data;
            this.showDialog = "info";
            return;
          }
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    showPermissionsAndRolesDialog(user) {
      this.currentUser = user;
      this.currentUserRoles = user.userRoles ? user.userRoles.slice(0) : [];
      this.currentPermissions = (user.permissions || []).map((permission) => ({
        ...permission,
      }));
      this.currentPermissionRole = null;
      this.currentPermissionEntity = {};
      this.currentPermissionEntities = [];
      this.dialogError = "";
      this.showDialog = "permissionsAndRoles";
    },
    addCurrentUserPermission() {
      return this.addPermissionToList(this.currentPermissions);
    },
    removeCurrentUserPermission(index) {
      this.currentPermissions.splice(index, 1);
    },
    userRolesChanged() {
      var originalUserRoles = this.currentUser.userRoles || [];
      if (originalUserRoles.length != this.currentUserRoles.length) {
        return true;
      }
      return originalUserRoles.some(
        (userRole) => !this.currentUserRoles.includes(userRole)
      );
    },
    savePermissionsAndRoles() {
      if (!this.takeOverSelectedPermission(this.currentPermissions)) {
        return;
      }
      var userId = this.currentUser.id;
      var originalPermissions = this.currentUser.permissions || [];
      var requests = [];
      if (this.userRolesChanged()) {
        requests.push(() =>
          ServerConnection.put("admin/user/" + userId + "/userRoles", {
            userRoles: this.currentUserRoles,
          })
        );
      }
      originalPermissions
        .filter(
          (permission) =>
            !this.currentPermissions.some((kept) => kept.id == permission.id)
        )
        .forEach((permission) =>
          requests.push(() =>
            ServerConnection.delete(
              "admin/user/" + userId + "/permission/" + permission.id
            )
          )
        );
      this.currentPermissions
        .filter((permission) => !permission.id)
        .forEach((permission) =>
          requests.push(() =>
            ServerConnection.put("admin/user/" + userId + "/permission", permission)
          )
        );
      if (requests.length < 1) {
        this.showDialog = "";
        return;
      }
      requests
        .reduce((chain, request) => chain.then(request), Promise.resolve())
        .then(() => {
          this.showDialog = "";
          this.loadUsers(true);
        })
        .catch((error) => {
          this.loadUsers(true);
          if (error.response && error.response.status == 400) {
            this.dialogError = error.response.data;
            return;
          }
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    closeDialog() {
      var returnDialog = this.returnDialog;
      this.returnDialog = "";
      if (returnDialog == "contactPersonsWithoutUser") {
        this.showContactPersonsWithoutUserDialog();
        return;
      }
      this.showDialog = "";
    },
    showNewUserDialog() {
      this.newUser = { userName: "", password: "", permissions: [], contactPerson: null };
      this.currentPermissionRole = null;
      this.currentPermissionEntity = {};
      this.currentPermissionEntities = [];
      this.dialogError = "";
      this.returnDialog = "";
      this.showDialog = "newUser";
      this.loadPermissionRoles();
      this.loadContactPersons();
      this.generatePasswordFor("newUser");
    },
    showConfirmAssignmentDialog(contactPersonWithoutUser) {
      var users = contactPersonWithoutUser.managingUsers || [];
      this.pendingAssignment = {
        contactPersonId: contactPersonWithoutUser.id,
        contactPersonName: contactPersonWithoutUser.name,
        users: users,
        user: users.length > 0 ? users[0] : null,
      };
      this.dialogError = "";
      this.showDialog = "confirmAssignment";
      this.returnDialog = "contactPersonsWithoutUser";
    },
    confirmAssignment() {
      if (!this.pendingAssignment.user) {
        this.dialogError = "Bitte einen Benutzer auswählen.";
        return;
      }
      ServerConnection.put(
        "admin/user/" + this.pendingAssignment.user.id + "/contactPerson",
        { contactPersonId: this.pendingAssignment.contactPersonId }
      )
        .then(() => {
          this.loadUsers(true);
          this.closeDialog();
        })
        .catch((error) => {
          if (error.response && error.response.status == 400) {
            this.dialogError = error.response.data;
            return;
          }
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    showNewUserDialogForContactPerson(contactPersonWithoutUser) {
      this.showNewUserDialog();
      this.returnDialog = "contactPersonsWithoutUser";
      this.newUser.permissions = (contactPersonWithoutUser.teams || []).map((team) => ({
        roleName: "TEAM_MANAGER",
        entitiyId: team.id,
        entityRepresentation: team.representation,
      }));
      this.loadContactPersons().then((contactPersons) => {
        this.newUser.contactPerson =
          contactPersons.find(
            (contactPerson) => contactPerson.id == contactPersonWithoutUser.id
          ) || null;
      });
      ServerConnection.get(
        "admin/contactPerson/" + contactPersonWithoutUser.id + "/userNameSuggestion"
      )
        .then((response) => {
          this.newUser.userName = response.data.userName;
        })
        .catch((error) => {
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    loadContactPersons() {
      if (!this.contactPersonsRequest) {
        this.contactPersonsRequest = ServerConnection.get("admin/contactPersons")
          .then((response) => {
            this.contactPersons = response.data;
            return this.contactPersons;
          })
          .catch((error) => {
            this.contactPersonsRequest = null;
            FailureHandler.handleServerFailure(this.$router, error);
            return [];
          });
      }
      return this.contactPersonsRequest;
    },
    showContactPersonsWithoutUserDialog() {
      this.returnDialog = "";
      this.contactPersonSearchTerm = "";
      ServerConnection.get("admin/contactPersons/withoutUser")
        .then((response) => {
          this.contactPersonsWithoutUser = response.data;
          this.showDialog = "contactPersonsWithoutUser";
        })
        .catch((error) => {
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    loadUserRoles() {
      if (this.userRoles.length <= 0) {
        ServerConnection.get("admin/userRoles")
          .then((response) => (this.userRoles = response.data))
          .catch((error) => {
            FailureHandler.handleServerFailure(this.$router, error);
          });
      }
    },
    getUserRoleLabels(user) {
      if (!user.userRoles) {
        return [];
      }
      return user.userRoles.map((roleName) => {
        var userRole = this.userRoles.find((candidate) => candidate.name == roleName);
        return userRole ? userRole.label : roleName;
      });
    },
    showAssignContactPersonDialog(user) {
      this.currentUser = user;
      this.currentContactPerson = null;
      this.dialogError = "";
      this.loadContactPersons().then((contactPersons) => {
        this.currentContactPerson =
          contactPersons.find(
            (contactPerson) => contactPerson.id == user.contactPersonId
          ) || null;
        this.showDialog = "assignContactPerson";
      });
    },
    assignContactPerson() {
      if (!this.currentContactPerson) {
        this.dialogError = "Bitte eine Kontaktperson auswählen.";
        return;
      }
      ServerConnection.put("admin/user/" + this.currentUser.id + "/contactPerson", {
        contactPersonId: this.currentContactPerson.id,
      })
        .then(() => {
          this.showDialog = "";
          this.loadUsers(true);
        })
        .catch((error) => {
          if (error.response && error.response.status == 400) {
            this.dialogError = error.response.data;
            return;
          }
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    addPermissionToList(permissions) {
      if (!this.currentPermissionRole) {
        this.dialogError = "Bitte einen Rechtetyp auswählen.";
        return false;
      }
      var permission = {
        roleName: this.currentPermissionRole.name,
      };
      if (this.currentPermissionRole.entityRequired) {
        if (!this.currentPermissionEntity || !this.currentPermissionEntity.id) {
          this.dialogError = "Für dieses Recht muss ein Eintrag ausgewählt werden.";
          return false;
        }
        permission["entitiyId"] = this.currentPermissionEntity.id;
        permission["entityRepresentation"] = this.currentPermissionEntity.representation;
      }
      var alreadyAdded = permissions.some(
        (existing) =>
          existing.roleName == permission.roleName &&
          existing.entitiyId == permission.entitiyId
      );
      if (!alreadyAdded) {
        permissions.push(permission);
      }
      this.dialogError = "";
      this.currentPermissionRole = null;
      this.currentPermissionEntity = {};
      this.currentPermissionEntities = [];
      return true;
    },
    addNewUserPermission() {
      return this.addPermissionToList(this.newUser.permissions);
    },
    takeOverSelectedPermission(permissions) {
      if (!this.currentPermissionRole) {
        return true;
      }
      return this.addPermissionToList(permissions);
    },
    removeNewUserPermission(index) {
      this.newUser.permissions.splice(index, 1);
    },
    createNewUser() {
      if (!this.newUser.userName || this.newUser.userName.trim().length < 1) {
        this.dialogError = "Bitte einen Benutzernamen angeben.";
        return;
      }
      if (!this.newUser.password || this.newUser.password.trim().length < 1) {
        this.dialogError = "Bitte ein Passwort angeben oder generieren lassen.";
        return;
      }
      if (!this.takeOverSelectedPermission(this.newUser.permissions)) {
        return;
      }
      ServerConnection.put("admin/user", {
        userName: this.newUser.userName.trim(),
        password: this.newUser.password,
        permissions: this.newUser.permissions,
        contactPersonId: this.newUser.contactPerson
          ? this.newUser.contactPerson.id
          : null,
      })
        .then(() => {
          this.loadUsers(true);
          this.closeDialog();
        })
        .catch((error) => {
          if (error.response && error.response.status == 400) {
            this.dialogError = error.response.data;
            return;
          }
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    showCredentialsMailDialog(user) {
      this.currentUser = user;
      this.dialogError = "";
      this.credentialsMail = {};
      ServerConnection.get("admin/user/" + user.id + "/credentialsMail")
        .then((response) => {
          var defaults = response.data;
          this.credentialsMail = {
            password: defaults.password,
            mailAddress: defaults.mailAddress,
            contactPersonName: defaults.contactPersonName,
            contactPersonMailAddress: defaults.contactPersonMailAddress,
            storeMailAddressForUser:
              !!defaults.mailAddress && defaults.mailAddress != defaults.userMailAddress,
            storeMailAddressForContactPerson: false,
          };
          this.showDialog = "credentialsMail";
        })
        .catch((error) => {
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    sendCredentialsMail() {
      if (!this.credentialsMail.password || this.credentialsMail.password.trim().length < 1) {
        this.dialogError = "Bitte ein Passwort angeben oder generieren lassen.";
        return;
      }
      if (
        !this.credentialsMail.mailAddress ||
        this.credentialsMail.mailAddress.indexOf("@") < 0
      ) {
        this.dialogError = "Bitte eine gültige E-Mail-Adresse angeben.";
        return;
      }
      this.credentialsMailIsSending = true;
      var mailAddress = this.credentialsMail.mailAddress.trim();
      ServerConnection.post("admin/user/" + this.currentUser.id + "/credentialsMail", {
        password: this.credentialsMail.password,
        mailAddress: mailAddress,
        storeMailAddressForUser: this.credentialsMail.storeMailAddressForUser,
        storeMailAddressForContactPerson: this.credentialsMail
          .storeMailAddressForContactPerson,
      })
        .then(() => {
          this.credentialsMailIsSending = false;
          this.infoMessage =
            "Die Zugangsdaten wurden an " +
            mailAddress +
            " gesendet. Das Passwort des Benutzers wurde auf den angegebenen Wert gesetzt.";
          this.showDialog = "info";
        })
        .catch((error) => {
          this.credentialsMailIsSending = false;
          if (error.response && (error.response.status == 400 || error.response.status == 500)) {
            this.dialogError = error.response.data;
            return;
          }
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    generatePasswordFor(target) {
      ServerConnection.get("admin/user/passwordSuggestion")
        .then((response) => {
          this[target].password = response.data.password;
        })
        .catch((error) => {
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    currentPermissionRoleChanged() {
      this.currentPermissionEntities = [];
      if (this.currentPermissionRole && this.currentPermissionRole.entityRequired) {
        ServerConnection.get(
          "admin/permission/entities/" + this.currentPermissionRole.name
        )
          .then((response) => (this.currentPermissionEntities = response.data))
          .catch((error) => {
            FailureHandler.handleServerFailure(this.$router, error);
          });
      }
    },
    loadUsers(forceIt) {
      if (forceIt || this.users.length <= 0) {
        this.usersLoading = true;
        ServerConnection.get("admin/users")
          .then((response) => (this.users = response.data))
          .catch((error) => {
            FailureHandler.handleServerFailure(this.$router, error);
          })
          .finally(() => (this.usersLoading = false));
      }
    },
    loadPermissionRoles() {
      if (this.permissionRoles.length <= 0) {
        ServerConnection.get("admin/permission/roles")
          .then((response) => {
            this.permissionRoles = response.data
              .map((role) => ({
                ...role,
                displayName: this.translatePermissionRole(role.name),
              }))
              .sort((a, b) => a.displayName.localeCompare(b.displayName));
          })
          .catch((error) => {
            FailureHandler.handleServerFailure(this.$router, error);
          });
      }
    },
    userMatchesSearchTerm(user, term) {
      if (!user) {
        return false;
      }
      var searchableTexts = [user.userName, user.contactPersonName, user.contactPersonTeams];
      this.getUserRoleLabels(user).forEach((label) => searchableTexts.push(label));
      if (user.permissions) {
        user.permissions.forEach((permission) =>
          searchableTexts.push(this.formatPermissionLabel(permission))
        );
      }
      return searchableTexts.some(
        (text) => text && text.toLowerCase().includes(term)
      );
    },
    formatDateTimeInfo(date) {
      if (date && date.length > 0) {
        var pattern = /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/;
        return date.replace(pattern, "$3.$2.$1 $4:$5 Uhr");
      }
      return "";
    },
    getPermissionCount(user) {
      return user && user.permissions ? user.permissions.length : 0;
    },
    getPermissionPreviewItems(user) {
      const maxItems = 3;
      if (!user || !user.permissions || user.permissions.length < 1) {
        return [];
      }
      return this.getSortedPermissions(user)
        .slice(0, maxItems)
        .map((permission) => this.formatPermissionLabel(permission));
    },
    getPermissionOverflowCount(user) {
      const maxItems = 3;
      const count = this.getPermissionCount(user);
      return count > maxItems ? count - maxItems : 0;
    },
    formatPermissionLabel(permission) {
      if (!permission) {
        return "";
      }
      const roleLabel = this.translatePermissionRole(permission.roleName);
      if (permission.entityRepresentation) {
        return roleLabel + " (" + permission.entityRepresentation + ")";
      }
      return roleLabel;
    },
    getSortedPermissions(user) {
      if (!user || !user.permissions) {
        return [];
      }
      return user.permissions
        .slice()
        .sort((a, b) =>
          this.translatePermissionRole(a.roleName).localeCompare(
            this.translatePermissionRole(b.roleName)
          )
        );
    },
    translatePermissionRole(roleName) {
      if (!roleName) {
        return "";
      }
      return PERMISSION_ROLE_LABELS[roleName] || roleName;
    },
    loadPermissionEntities(type) {
      ServerConnection.get("admin/permission/entities/" + type)
        .then((response) => (this.currentPermissionEntities = response.data))
        .catch((error) => {
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    updateMainMenu() {
      const menuService = useMenuService();
      menuService.setSidebarVisisble(false);
      menuService.setMainMenuItemNames([
        {
          name: "return",
          command: () => {
            if (window.history.length > 1) {
              this.$router.go(-1);
            } else {
              this.$router.push("/");
            }
          },
        },
        {
          name: "newUser",
          command: () => {
            this.showNewUserDialog();
          },
        },
      ]);
    },
  },
};
</script>

<style scoped>
.user-management {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 10px 0;
  flex-wrap: wrap;
}
.search-field {
  min-width: 260px;
  flex: 1 1 340px;
}
.search-field ::v-deep(input) {
  width: 100%;
}
.user-count {
  margin-left: auto;
  font-size: 0.9rem;
  color: #666;
}
.permission-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.permission-chip {
  background: #eef3f8;
  border: 1px solid #d7e1eb;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 0.8rem;
  line-height: 1.3;
}
.permission-overflow {
  color: #666;
  font-size: 0.8rem;
}
.contact-person {
  display: flex;
  flex-direction: column;
}
.contact-person-teams {
  color: #666;
  font-size: 0.8rem;
}
.dialog-field {
  margin: 10px 0px;
}
.dialog-field label {
  display: block;
  margin-bottom: 4px;
}
.dialog-field.checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.dialog-field.checkbox-row label {
  margin-bottom: 0;
}
.password-row {
  display: flex;
  align-items: center;
  gap: 4px;
}
.dialog-section {
  margin: 16px 0px 4px 0px;
}
.dialog-hint {
  color: #666;
  font-size: 0.85rem;
}
.pending-permission {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid #eef3f8;
  padding: 2px 0;
}
.user-role-option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0px;
}

.dialog-error {
  margin-top: 10px;
  color: #b00020;
  font-size: 0.85rem;
}
.dialog-warning {
  margin-top: 10px;
  color: #8a5300;
  font-size: 0.9rem;
}
</style>
